import React, { useState, useCallback, useMemo } from "react";
import Checkbox from "../../../Checkbox"; 

const DynamicAnalyticsDataRow = React.memo(({
  item,
  isSelected,
  onToggleSelect,
  activeColumns,
  columnDefs,
}) => {
  const rowActions = {
    isSelected,
    onToggleSelect,
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors cursor-default border-b border-[#0000001A]">
      {activeColumns.map((col) => columnDefs[col.name]?.cell(item, rowActions))}
    </tr>
  );
});

const KnowledgeGapsDataTable = ({
  data = [],
  loading = false,
  selectedRows = [],
  onSelectAll,
  onSelectRow,
  columnPreferences,
  onViewResponse, 
}) => {
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const savedWidths = localStorage.getItem("knowledgeGapsTable_columnWidths_v2");
      return savedWidths ? JSON.parse(savedWidths) : {};
    } catch (error) {
      return {};
    }
  });

  const handleResize = useCallback((colName, e) => {
    e.preventDefault();
    e.stopPropagation();

    const thElement = e.target.closest("th");
    const startWidth = thElement ? thElement.getBoundingClientRect().width : 150;
    const startX = e.clientX;

    const onMouseMove = (moveEvent) => {
      const minW = colName === "user_query" ? 250 : 100;
      const newWidth = Math.max(minW, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [colName]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "default";

      setColumnWidths((latestWidths) => {
        localStorage.setItem("knowledgeGapsTable_columnWidths_v2", JSON.stringify(latestWidths));
        return latestWidths;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
  }, []);

  const getColStyle = useCallback((colName) => {
    return columnWidths[colName]
      ? { width: columnWidths[colName], minWidth: columnWidths[colName], maxWidth: columnWidths[colName] }
      : {};
  }, [columnWidths]);

  const isAllChecked = data.length > 0 && selectedRows.length === data.length;

  const defaultCols = [
    { name: "timestamp", visible: true, order: 0 },
    { name: "user_query", visible: true, order: 1 },
    { name: "context", visible: true, order: 2 },
    { name: "response", visible: true, order: 3 },
    { name: "chat_score", visible: true, order: 4 },
  ];

  const activeColumns = useMemo(() => {
    return (columnPreferences && columnPreferences.length > 0 ? columnPreferences : defaultCols)
      .filter((col) => col.visible === true || col.visible === "true")
      .sort((a, b) => a.order - b.order);
  }, [columnPreferences]);

  const visibleColCount = activeColumns.length;

  const columnDefs = useMemo(() => ({
    timestamp: {
      header: (
        <th
          key="timestamp"
          style={getColStyle("timestamp")}
          className={`${!columnWidths.timestamp ? "w-[220px] min-w-[220px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          <div className="flex items-center gap-3 w-full h-full">
            <div className="shrink-0 w-[24px]">
              <Checkbox checked={isAllChecked} onChange={onSelectAll} disabled={data.length === 0} showLabel={false} size="md" />
            </div>
            <span className="truncate block pr-2">Date & Time</span>
          </div>
          <div onMouseDown={(e) => handleResize("timestamp", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item, rowActions) => (
        <td
          key={`timestamp-${item.id}`}
          style={getColStyle("timestamp")}
          className={`${!columnWidths.timestamp ? "w-[220px] min-w-[220px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium border-b border-r border-[#0000001A] bg-white`}
        >
          <div className="flex items-center gap-3 w-full h-full min-w-0">
            <div className="shrink-0 w-[24px]" onClick={(e) => e.stopPropagation()}>
              <Checkbox id={`checkbox-${item.id}`} checked={rowActions.isSelected} onChange={(val, e) => { if (e) e.stopPropagation(); rowActions.onToggleSelect(item.id); }} disabled={!item.id} showLabel={false} size="md" />
            </div>
            <span className="truncate block pr-2" title={item.timestamp}>{item.timestamp || "_"}</span>
          </div>
        </td>
      ),
    },
    user_query: {
      header: (
        <th 
          key="user_query" 
          style={getColStyle("user_query")} 
          className={`${!columnWidths.user_query ? "w-[30%] min-w-[250px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          User Query
          <div onMouseDown={(e) => handleResize("user_query", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td key={`user_query-${item.id}`} style={getColStyle("user_query")} className={`${!columnWidths.user_query ? "w-[30%] min-w-[250px]" : ""} px-3 py-3 text-[#52525B] font-outfit text-[14px] font-medium border-b border-r border-[#0000001A] bg-white text-left`}>
          <div className="line-clamp-2" title={item.user_query}>{item.user_query || "-"}</div>
        </td>
      ),
    },
    context: {
      header: (
        <th 
          key="context" 
          style={getColStyle("context")} 
          className={`${!columnWidths.context ? "w-[180px] min-w-[180px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          Context
          <div onMouseDown={(e) => handleResize("context", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td 
          key={`context-${item.id}`} 
          style={getColStyle("context")} 
          className={`${!columnWidths.context ? "w-[180px] min-w-[180px]" : ""} px-3 py-3 align-middle border-b border-r border-[#0000001A] bg-white min-w-0 text-left`}
        >
          <span 
            className="px-3 py-1 border border-[#DEDEDE] rounded-full text-[#52525B] text-[13px] font-medium font-outfit bg-white inline-block max-w-full truncate align-middle"
            title={item.context}
          >
            {item.context}
          </span>
        </td>
      ),
    },
    response: {
      header: (
        <th 
          key="response" 
          style={getColStyle("response")} 
          className={`${!columnWidths.response ? "w-[140px] min-w-[140px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          AI Response
          <div onMouseDown={(e) => handleResize("response", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td key={`response-${item.id}`} style={getColStyle("response")} className={`${!columnWidths.response ? "w-[140px] min-w-[140px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium truncate border-b border-r border-[#0000001A] bg-white`}>
          <span 
            className="text-[#C95EBE] font-outfit text-[14px] font-medium hover:underline whitespace-nowrap cursor-pointer"
            onClick={() => onViewResponse(item)}
          >
            View Response
          </span>
        </td>
      ),
    },
    chat_score: {
      header: (
        <th 
          key="chat_score" 
          style={getColStyle("chat_score")} 
          className={`${!columnWidths.chat_score ? "w-[120px] min-w-[120px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          Chat Score
          <div onMouseDown={(e) => handleResize("chat_score", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td key={`chat_score-${item.id}`} style={getColStyle("chat_score")} className={`${!columnWidths.chat_score ? "w-[120px] min-w-[120px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium border-b border-[#0000001A] bg-white`}>
          {item.chat_score}
        </td>
      ),
    },
  }), [columnWidths, getColStyle, handleResize, isAllChecked, onSelectAll, data.length, onViewResponse]);

  return (
    <div className="w-full h-full overflow-auto custom-scrollbar relative">
      <table className="w-full min-w-[1000px] table-fixed text-sm text-left border-separate border-spacing-0">
        <thead className="bg-[#F7F7F7] relative z-[50]">
          <tr>
            {activeColumns.map((col) => columnDefs[col.name]?.header)}
          </tr>
        </thead>
        <tbody className="bg-white">
          {loading ? (
            <tr>
              <td colSpan={visibleColCount} className="py-24 text-center align-middle border-b border-[#0000001A] bg-white relative">
                <div className="flex flex-col items-center justify-center w-full h-full min-h-[200px]">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C95EBE] mx-auto mb-4"></div>
                  <p className="text-[#52525B] font-outfit text-[15px]">Loading table data...</p>
                </div>
              </td>
            </tr>
          ) : data.length > 0 ? (
            data.map((item) => (
              <DynamicAnalyticsDataRow
                key={item.id}
                item={item}
                isSelected={selectedRows.includes(item.id)}
                onToggleSelect={onSelectRow}
                activeColumns={activeColumns}
                columnDefs={columnDefs}
              />
            ))
          ) : (
            <tr>
              <td colSpan={visibleColCount} className="py-10 text-center text-black border-b border-[#0000001A]">
                <img src="/no-contracts.svg" alt="No Data" className="mx-auto mb-2" />
                <p className="text-[16px] font-outfit">Looks like your table is empty!</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default KnowledgeGapsDataTable;